import {openStore} from './store';

const cachePromises = {};

// A persistent cache backed by IndexedDB
export const makePersistentCache = async ({dbName, storeName, schemaVersion, options}) => {
	// In case multiple clients want caches for the same store, we use the previously created promise to ensure
	// they all end up with the same one.  They will all get back the same resolved promise.
	const dbKey = `${dbName}_${storeName}_${schemaVersion}`;
	if (cachePromises[dbKey]) return cachePromises[dbKey];

	const creationPromise = new Promise((resolve, reject) => {
		const map = new Map();

		// Persistent cache
		openStore({dbName, storeName, schemaVersion, options})
			.then(store => {
				let inMemoryCacheHits = 0;
				let persistentCacheHits = 0;

				// Return an object with the interface methods we expose
				const self = {
					name() {
						return dbName;
					},

					version() {
						return schemaVersion;
					},

					async has(key) {
						return map.has(key) || (await store.has(key));
					},

					async set(key, value) {
						// log(`cache.set('${key}', '${value}')`);
						// NOTE: We always set directly into the in-memory Map, never into the underlying IndexedDB
						map.set(key, value);
					},

					async get(key, defaultValue = undefined) {
						// log(`cache.get(${key},${defaultValue})`);
						let value = map.get(key);
						if (value !== undefined) {
							inMemoryCacheHits += 1;
							// log(`inMemoryCacheHits: ${inMemoryCacheHits}`);
							return value;
						}

						// Check the persistent cache
						value = await store.get(key, defaultValue);
						if (value !== undefined) {
							persistentCacheHits += 1;
							// log(`persistentCacheHits: ${persistentCacheHits}`);
							return value;
						}

						return defaultValue;
					},

					async getAll(includeMetadata = false) {
						// Get the persistent entries first
						const result = await store.getAll(includeMetadata);

						// In-memory cache entries will overwrite persistent entries
						for (const [key, value] of map.entries()) result[key] = value;

						return result;
					},

					// Delete from both the in-memory and persistent caches
					async delete(key) {
						await store.delete(key);
						map.delete(key);
					},

					async clear() {
						map.clear();
						await store.clear();
						inMemoryCacheHits = 0;
						persistentCacheHits = 0;
					},

					close() {
						self.clear();
						store.close();
					},

					// Force all locally cached values into the database
					async persist() {
						const entries = map.entries();
						await store.setMultiple(entries);
						map.clear();
					},

					inMemorySize() {
						return map.size;
					},

					async persistedSize() {
						return await store.size();
					},

					inMemoryCacheHits() {
						return inMemoryCacheHits;
					},

					persistentCacheHits() {
						return persistentCacheHits;
					}
				};

				resolve(self);
			})
			.catch(e => {
				reject(e);
			});
	});

	// Remember this promise so we can return it again if another caller asks for the same cache
	cachePromises[dbKey] = creationPromise;
	return creationPromise;
};
